import { cx } from "@emotion/css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { ProgressCircle } from "@/modules/theme/components/ProgressCircle/ProgressCircle";
import { StepStatus } from "@/modules/theme/components/StepStatus/StepStatus";
import { Typography } from "@/modules/theme/components/Typography/Typography";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { ParsedTutorial } from "@/modules/tutorials/types/ParsedTutorial";

import { createTutorialItemStyles } from "./createTutorialItemStyles";
import { ReactComponent as TutorialDoneIcon } from "./tutorial-done-icon.svg";

const translationNamespace = "pages/TutorialsPage";

type TutorialItemProps = {
  tutorial: ParsedTutorial;
};

export const TutorialItem = ({ tutorial }: TutorialItemProps) => {
  const classes = createTutorialItemStyles(useTheme());
  const { t } = useTranslation(translationNamespace);
  const areAllStepsDone = tutorial.stepsCompleted === tutorial.stepsNumber;

  const tutorialProgressStepsCompleted =
    tutorial.stepsCompleted + (tutorial.testPassed ? 1 : 0);
  const tutorialProgressStepsSum =
    tutorial.stepsNumber + (tutorial.testQuestionsNumber ? 1 : 0);

  const tutorialProgress =
    (tutorialProgressStepsCompleted / tutorialProgressStepsSum) * 100;

  const nextStep =
    tutorial.stepsCompleted + 1 > tutorial.stepsNumber
      ? 1
      : tutorial.stepsCompleted + 1;

  return (
    <Link
      className={classes.root}
      to={authenticatedRouting.buildUrl("tutorialDetails", {
        params: {
          id: tutorial.id,
          step: `${nextStep}`,
        },
      })}
    >
      <ProgressCircle size={110} strokeWidth={6} progress={tutorialProgress}>
        <div className={classes.content}>
          <span className={cx(classes.point, classes.pointSteps)}>
            <StepStatus done={areAllStepsDone} />
            {tutorial.stepsCompleted}
            <span className={classes.totalStepsNumber}>
              /{tutorial.stepsNumber}
            </span>
          </span>
          {tutorial.testQuestionsNumber ? (
            <span className={classes.point}>
              <StepStatus done={tutorial.testPassed} />
              {t("Test")}
            </span>
          ) : null}
        </div>
        {areAllStepsDone &&
          (tutorial.testPassed || tutorial.testQuestionsNumber === 0) && (
            <TutorialDoneIcon className={classes.tutorialDoneIcon} />
          )}
      </ProgressCircle>
      <Typography
        variant="subheading"
        Component="p"
        className={classes.tutorialName}
      >
        {tutorial.name}
      </Typography>
    </Link>
  );
};
